import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { api, fileUrl } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Gift, MapPin, List, Search, ArrowLeft } from "lucide-react";
import "./MapView.css";

// Custom orange "gift" marker matching our brand. Default Leaflet markers ship
// as PNG/SVG via webpack and don't bundle well in our CRA — using a divIcon
// with inline SVG sidesteps that entirely.
const giftIcon = L.divIcon({
  className: "gift-marker",
  html: `<div class="gift-marker-pin">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 12v10H4V12M2 7h20v5H2V7zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"
        stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    </svg>
  </div>`,
  iconSize: [36, 44],
  iconAnchor: [18, 44],
  popupAnchor: [0, -40],
});

// Lithuania bounding box-ish center.
const DEFAULT_CENTER = [55.3, 23.9];
const DEFAULT_ZOOM = 7;

function FitToMarkers({ listings }) {
  // Whenever the listing list changes, re-fit the map so all pins are visible.
  const map = useMap();
  useEffect(() => {
    const valid = listings.filter((l) => l.lat && l.lng);
    if (valid.length === 0) return;
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 12);
      return;
    }
    const bounds = L.latLngBounds(valid.map((l) => [l.lat, l.lng]));
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 13 });
  }, [listings, map]);
  return null;
}

export default function MapView() {
  const { t, lang } = useI18n();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get("/listings", { params: { listing_type: "free", limit: 500 } })
      .then((r) => { if (alive) setListings(r.data || []); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Page-level SEO so this lands well in Google.
  useEffect(() => {
    document.title = "Atiduodu — žemėlapis | StopChlamas";
    const desc = "Visi nemokamai atiduodami daiktai visoje Lietuvoje žemėlapyje. Surask, kas atiduodama netoli tavęs.";
    const el = document.querySelector('meta[name="description"]');
    if (el) el.setAttribute("content", desc);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return listings;
    return listings.filter(
      (l) =>
        (l.title || "").toLowerCase().includes(q) ||
        (l.city || "").toLowerCase().includes(q) ||
        (l.description || "").toLowerCase().includes(q)
    );
  }, [listings, search]);

  const labels = TEXTS[lang] || TEXTS.lt;
  const withCoords = filtered.filter((l) => l.lat && l.lng);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="map-view">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-4">
        <div className="flex-1">
          <Link
            to="/atiduodu"
            className="inline-flex items-center gap-1 text-sm text-[#7A7A73] hover:text-[#C85A32] mb-2"
          >
            <ArrowLeft className="w-4 h-4" /> {labels.back_to_list}
          </Link>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight text-[#1A1A18] inline-flex items-center gap-3">
            <Gift className="w-8 h-8 text-[#C85A32]" />
            {labels.h1}
          </h1>
          <p className="mt-2 text-sm text-[#4A4A45]">{labels.subtitle}</p>
        </div>
        <div className="flex items-center gap-2 sm:items-end">
          <Link
            to="/atiduodu"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm bg-white border border-[#D1CFC7] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
            data-testid="map-to-list"
          >
            <List className="w-4 h-4" /> {labels.list_view}
          </Link>
        </div>
      </div>

      <div className="relative card-surface overflow-hidden p-0">
        <div className="absolute top-3 left-3 right-3 z-[400] flex items-center gap-2 pointer-events-none">
          <div className="bg-white/95 backdrop-blur rounded-md shadow-md px-3 py-2 flex items-center gap-2 flex-1 max-w-md pointer-events-auto">
            <Search className="w-4 h-4 text-[#7A7A73]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={labels.search_placeholder}
              data-testid="map-search-input"
              className="bg-transparent outline-none text-sm flex-1 placeholder:text-[#9A9A93]"
            />
          </div>
          <div className="bg-white/95 backdrop-blur rounded-md shadow-md px-3 py-2 text-sm font-semibold text-[#1A1A18] pointer-events-auto">
            {labels.count_prefix}: <span data-testid="map-count">{withCoords.length}</span>
          </div>
        </div>

        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom
          className="map-canvas"
          attributionControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitToMarkers listings={withCoords} />
          {withCoords.map((l) => (
            <Marker key={l.id} position={[l.lat, l.lng]} icon={giftIcon}>
              <Popup>
                <ListingPopup listing={l} labels={labels} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {!loading && withCoords.length === 0 && (
        <div
          className="mt-6 bg-[#F0EFEB] border border-[#D1CFC7] rounded-lg p-8 text-center"
          data-testid="map-empty"
        >
          <Gift className="w-10 h-10 text-[#C85A32] mx-auto" />
          <h2 className="mt-3 font-heading font-bold text-xl text-[#1A1A18]">{labels.empty_title}</h2>
          <p className="mt-2 text-[#4A4A45]">{labels.empty_desc}</p>
          <Link to="/post" className="mt-4 inline-block btn-primary">
            {labels.empty_cta}
          </Link>
        </div>
      )}

      <p className="mt-4 text-xs text-[#7A7A73]">{labels.disclaimer}</p>
    </div>
  );
}

function ListingPopup({ listing, labels }) {
  const img = listing.images?.[0];
  return (
    <div className="map-popup" data-testid={`map-popup-${listing.id}`}>
      {img && (
        <div className="map-popup-img-wrap">
          <img src={fileUrl(img)} alt={listing.title} className="map-popup-img" loading="lazy" />
        </div>
      )}
      <div className="map-popup-body">
        <h3 className="map-popup-title">{listing.title}</h3>
        <p className="map-popup-meta">
          <MapPin className="w-3 h-3 inline -mt-0.5" /> {listing.city}
        </p>
        <span className="map-popup-tag">🆓 {labels.free_label}</span>
        <Link to={`/listing/${listing.id}`} className="map-popup-cta" data-testid="map-popup-open">
          {labels.open_listing} →
        </Link>
      </div>
    </div>
  );
}

const TEXTS = {
  lt: {
    h1: "Atiduodu — žemėlapis",
    subtitle: "Visi nemokamai atiduodami daiktai Lietuvoje. Surask, kas atiduodama netoli tavęs.",
    back_to_list: "← Atgal į „Atiduodu\"",
    list_view: "Sąrašo vaizdas",
    search_placeholder: "Ieškoti pavadinime, mieste…",
    count_prefix: "Rasta",
    free_label: "Atiduodama",
    open_listing: "Atidaryti skelbimą",
    empty_title: "Nemokamų skelbimų dar nėra",
    empty_desc: "Būk pirmas — atiduok nereikalingą daiktą per stopchlamas.lt.",
    empty_cta: "Įkelti skelbimą",
    disclaimer: "Žymeklis rodo miestą, ne tikslų adresą. Adresą tarpusavyje suderink per žinutes.",
  },
  ru: {
    h1: "Отдаю — карта",
    subtitle: "Все бесплатные вещи в Литве. Найди то, что отдают рядом с тобой.",
    back_to_list: "← Назад в «Отдаю»",
    list_view: "Список",
    search_placeholder: "Поиск по названию, городу…",
    count_prefix: "Найдено",
    free_label: "Отдаю",
    open_listing: "Открыть объявление",
    empty_title: "Бесплатных объявлений пока нет",
    empty_desc: "Будь первым — отдай ненужное через stopchlamas.lt.",
    empty_cta: "Подать объявление",
    disclaimer: "Метка показывает город, не точный адрес. Адрес согласуйте через сообщения.",
  },
  en: {
    h1: "Free stuff — map",
    subtitle: "All free items in Lithuania. Find what's being given away near you.",
    back_to_list: "← Back to list",
    list_view: "List view",
    search_placeholder: "Search title, city…",
    count_prefix: "Found",
    free_label: "Free",
    open_listing: "Open listing",
    empty_title: "No free listings yet",
    empty_desc: "Be first — give away something unused via stopchlamas.lt.",
    empty_cta: "Post a listing",
    disclaimer: "Pins show the city, not the exact address. Arrange the pickup spot via messages.",
  },
};
